import moment from 'moment';
import { CLOUD_TYPE_IDS } from 'users/constants/usersConstants.js';
import DateFilter from 'shared/modules/dateFilter.js';
import { CostUsageStates } from 'usage/constants/costAndUsageConstants.js';

export const getFirstDayOfMonth = (date) => {
  return moment(date).startOf('month').format('YYYY-MM-DD');
};

export const getFirstDayOfYear = (date, day) => {
  return moment(date)
    .startOf('year')
    .add(day - 1, 'days')
    .format('YYYY-MM-DD');
};
export const getFirstAndLastDayOfPreviousMonth = () => {
  const firstDay = moment(DateFilter.getDate()).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
  const lastDay = moment(DateFilter.getDate()).subtract(1, 'months').endOf('month').format('YYYY-MM-DD');

  return {
    start: firstDay,
    end: lastDay,
  };
};

export const sumTotalCost = (data) => {
  return data?.reduce((acc, item) => acc + (item.total_cost || 0), 0);
};

export const compareFormatterNumber = (a, b) => {
  const numA = Number((a.split(' ')[1] || a.split(' ')[0]).replace(/,/g, ''));
  const numB = Number((b.split(' ')[1] || b.split(' ')[0]).replace(/,/g, ''));
  return numA - numB;
};
export const getFilterFields = (filters) => {
  const filterFields = {};
  Object.keys(filters).forEach((k) => {
    filterFields[k] = 1;
  });
  return JSON.stringify(filterFields);
};
export const getSearchParamsPanel = (params, isPpApplied) => {
  if (!params) {
    return '';
  }
  const filterParams = new URLSearchParams();

  filterParams.set('currCostType', JSON.stringify(params.costType));
  filterParams.set('fieldToFilterdValuesMap', JSON.stringify({}));
  filterParams.set('excludedFiltersStatusMap', JSON.stringify({}));
  filterParams.set('likeFiltersStatus', JSON.stringify({}));
  filterParams.set('startDate', params.start);
  filterParams.set('endDate', params.end);
  filterParams.set('currPeriodGranLevel', params.periodGranLevel);
  filterParams.set('selectedGranLevel', params.periodGranLevel);
  filterParams.set('currentGroupBy', params.groupBy);
  filterParams.set('filterBarGroupBy', params.groupBy);
  filterParams.set('isCumulative', false);
  filterParams.set('isPpApplied', isPpApplied);
  filterParams.set('isAmortize', params.isAmortized);
  filterParams.set('isShowAmortizeCost', params.isAmortized);
  filterParams.set('isDistributed', params.isDistrubuted);
  filterParams.set('isNetAmortize', params.isNetAmortized);
  filterParams.set('isNetUnblended', params.isNetUnblended);
  filterParams.set('isPublicCost', params.isPublicCost);
  filterParams.set('isUnblended', params.isUnblended);
  filterParams.set('isListUnitPrice', params.isListUnitPrice);

  if (params.secondaryGroupBy && params.secondaryGroupBy !== 'usagedate') {
    filterParams.set('filterBarGroupBySecondary', JSON.stringify(params.secondaryGroupBy));
    filterParams.set('currDataState', CostUsageStates.SECOND_GROUP_BY);
  }
  if (params.exclude) {
    filterParams.set('fieldToFilterdValuesMap', JSON.stringify({ ...params.exclude }));
    const excludeFilters = getFilterFields(params.exclude);
    filterParams.set('excludedFiltersStatusMap', excludeFilters);
  }
  return `?${filterParams.toString()}`;
};

export const getExcludeOptions = (usersStore) => {
  const { currDispUserCloudAccountType } = usersStore;
  const SUPPORT_OPTION = { value: 'support', label: 'Support' };
  const TAX_OPTION = { value: 'tax', label: 'Tax' };
  const MARKETPLACE_OPTION = { value: 'marketplace', label: 'Marketplace' };

  switch (currDispUserCloudAccountType) {
    case CLOUD_TYPE_IDS.AWS:
      return [SUPPORT_OPTION, TAX_OPTION, MARKETPLACE_OPTION];
    case CLOUD_TYPE_IDS.AZURE:
      return [MARKETPLACE_OPTION];
    case CLOUD_TYPE_IDS.GCP:
      return [];
    default:
      return null;
  }
};

export const getExcludeFilterParams = (filters) => {
  const params = new URLSearchParams();

  Object.keys(filters).forEach((key) => {
    filters[key].forEach((value) => {
      params.append(`excludeFilters[${key}]`, value);
    });
  });
  const queryString = params.toString();
  return queryString ? `&${queryString}` : '';
};

export const getGroupSettingByAccount = (usersStore, isViewingResellerAccountData = true) => {
  if (usersStore.isCurrentAccountAllAccounts) {
    return 'payeraccount';
  }
  if (usersStore.isCurrentAccountMulticloud && isViewingResellerAccountData) {
    return 'cloudprovider';
  }
  return 'none';
};

export const aggregateCostsByDate = (data, key) => {
  const dateMap = new Map();

  data.forEach(({ usage_date, total_cost }) => {
    if (dateMap.has(usage_date)) {
      dateMap.get(usage_date)[key] += parseFloat(total_cost);
    } else {
      dateMap.set(usage_date, {
        day: moment(usage_date).format('DD'),
        fullDate: usage_date,
        [key]: parseFloat(total_cost),
      });
    }
  });

  return Array.from(dateMap.values());
};

export const aggregateCostsByGroup = (data, includeLAName) => {
  const dataMap = new Map();
  data.forEach(({ group_by, total_cost, linked_account_name }) => {
    const currentTotal = dataMap.get(group_by)?.totalCost || 0;
    dataMap.set(group_by, {
      totalCost: currentTotal + parseFloat(total_cost),
      linkedAccountName: includeLAName ? linked_account_name : null,
    });
  });
  return dataMap;
};

export const processSingleAccountData = (
  data,
  forecastData,
  forecastNumMonth,
  forecastEmptyState,
  appStore,
  settingsData,
  includeForecast = true,
) => {
  const { dataKeys, accountKey, forecastKey } = settingsData;
  const { forecastEmptyStateText, setForecastEmptyStateText } = forecastEmptyState;
  const minValueText = 'Forecast not available (monthly historical spend must be at least $100)';
  if (!dataKeys.includes(accountKey)) {
    dataKeys.push(accountKey);
  }
  const historyMonths = data?.map((row, index) => {
    const previousValue = data[index - 1] ? data[index - 1].total_cost : 0;
    let percentValue = 0;
    if (index > 0) {
      percentValue = previousValue ? ((row.total_cost - previousValue) * 100) / previousValue : null;
    }
    return {
      [accountKey]: row.total_cost,
      usageDate: row.usage_date,
      percentChange: percentValue,
    };
  });
  if (appStore.isKeyCloakManagement && !forecastData?.forecastPerMonth) {
    if (
      historyMonths?.length === 0 ||
      (historyMonths[historyMonths.length - 1][accountKey] < 100 &&
        (historyMonths.length === 1 || historyMonths[historyMonths.length - 2][accountKey] < 100))
    ) {
      setForecastEmptyStateText(minValueText);
    } else if (forecastEmptyStateText === minValueText) {
      setForecastEmptyStateText('');
    }
  } else if (forecastEmptyStateText === minValueText) {
    setForecastEmptyStateText('');
  }
  const forecastMonths =
    includeForecast && forecastData?.forecastPerMonth
      ? Object.entries(forecastData?.forecastPerMonth)
          .map(([usageDate, value]) => ({
            usageDate,
            [forecastKey]: value,
          }))
          .slice(0, forecastNumMonth)
      : [];
  if (forecastMonths.length > 0 && !dataKeys.includes(forecastKey)) {
    dataKeys.push(forecastKey);
  }
  return [...historyMonths, ...forecastMonths];
};
export const processMultiAccountData = ({
  data,
  dataKeys,
  forecastData,
  forecastNumMonth,
  forecastKey,
  includeForecast = true,
}) => {
  // Step 1: Accumulate the total cost per group across all usage dates
  const totalCosts = data.reduce((acc, { group_by, total_cost }) => {
    acc[group_by] = (acc[group_by] || 0) + parseFloat(total_cost);
    return acc;
  }, {});

  // Step 2: Sort the groups by total cost and determine the top 5 groups
  const sortedGroups = Object.keys(totalCosts).sort((a, b) => totalCosts[b] - totalCosts[a]);
  const top5Groups = sortedGroups.slice(0, 5);

  // add 5 groups are in dataKeys for the legend
  top5Groups.forEach((group) => {
    if (!dataKeys.includes(group)) {
      dataKeys.push(group);
    }
  });
  if (sortedGroups.length > 5 && !dataKeys.includes('other')) {
    dataKeys.push('other');
  }

  // Step 3: For each usage date, aggregate the costs for the top 5 groups and combine the rest into an "other" category
  const historyData = data.reduce((acc, { group_by, usage_date, total_cost }) => {
    const dateEntry = acc.find((entry) => entry.usageDate === usage_date);
    if (dateEntry) {
      if (top5Groups.includes(group_by)) {
        dateEntry[group_by] = (dateEntry[group_by] || 0) + parseFloat(total_cost);
      } else {
        const otherValue = (dateEntry['other'] || 0) + parseFloat(total_cost);
        if (otherValue !== 0) {
          dateEntry['other'] = otherValue;
        }
      }
    } else {
      acc.push({
        usageDate: usage_date,
        [group_by]: top5Groups.includes(group_by) ? parseFloat(total_cost) : 0,
      });
    }
    return acc;
  }, []);

  const forecastMonths =
    includeForecast && forecastData?.forecastPerMonth
      ? Object.entries(forecastData?.forecastPerMonth)
          .map(([usageDate, value]) => ({
            usageDate,
            [forecastKey]: value,
          }))
          .slice(0, forecastNumMonth)
      : [];
  if (forecastMonths.length > 0 && !dataKeys.includes(forecastKey)) {
    dataKeys.push(forecastKey);
  }
  return [...historyData, ...forecastMonths];
};
